/**
 * Shared % / CHF override row + funding base selector.
 * Extracted for reuse by OutputPanel and FinancialDriversPanel.
 * Commits the same override shape: { type: 'rate'|'amount', value }.
 */
import { useState } from 'react';
import { formatMoney } from '../../utils/formatters';
import { ValueWithTooltip } from '../Tooltip';
import { draftOnFocus, isEffectivelyZero } from '../../utils/clearableNumber';
import { useDealCurrency } from '../../context/DealCurrencyContext';

export const OVERRIDE_GRID = 'grid grid-cols-[1fr_44px_82px] items-center';

export function SourceSelect({ value, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-[6px] bg-gray-100 border border-gray-200 rounded px-0.5 h-4 min-h-[16px] text-gray-500 cursor-pointer hover:border-blue-300"
      data-testid={testId}
      aria-label="Funding base source"
    >
      <option value="totalDevCost">Dev</option>
      <option value="softCosts">Soft</option>
      <option value="constructionCost">Build</option>
      <option value="landCost">Land</option>
    </select>
  );
}

export function EditableOverrideRow({
  label,
  rate,
  amount,
  fieldKey,
  onOverride,
  sub = false,
  baseName,
  sourceSelector,
  currency,
}) {
  const money = useDealCurrency();
  const cur = currency || money.op;
  const [editingRate, setEditingRate] = useState(false);
  const [editingAmount, setEditingAmount] = useState(false);
  const [tempRate, setTempRate] = useState('');
  const [tempAmount, setTempAmount] = useState('');
  const editable = typeof onOverride === 'function';

  const commitRate = () => {
    if (!editable) return;
    const v = parseFloat(tempRate);
    if (!Number.isNaN(v) && v >= 0) onOverride(fieldKey, { type: 'rate', value: v / 100 });
    setEditingRate(false);
  };
  const commitAmount = () => {
    if (!editable) return;
    const v = parseFloat(String(tempAmount).replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(v) && v >= 0) onOverride(fieldKey, { type: 'amount', value: v });
    setEditingAmount(false);
  };

  const rateDisplay = ((Number(rate) || 0) * 100).toFixed(1);
  const baseShort = baseName
    ? ({
      Revenue: 'Rev',
      'Build Total': 'Build',
      Construction: 'Build',
      Land: 'Land',
      PBT: 'PBT',
      'Dev Cost': 'Dev',
    }[baseName] || baseName)
    : null;

  return (
    <div
      className={`${OVERRIDE_GRID} leading-none ${sub ? 'pl-1.5' : ''}`}
      style={{ paddingTop: '1px', paddingBottom: '1px' }}
      data-testid={`override-row-${fieldKey}`}
    >
      <div className="flex items-center gap-0.5 min-w-0">
        <span className={`text-[9px] shrink-0 leading-none ${sub ? 'text-gray-400' : 'text-gray-700'}`}>
          {label}
        </span>
        {editable ? sourceSelector : null}
      </div>
      <div className="text-center">
        {editable && editingRate ? (
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={tempRate}
            onChange={(e) => {
              const raw = e.target.value.replace(',', '.');
              if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) setTempRate(raw);
            }}
            onFocus={(e) => {
              if (isEffectivelyZero(e.target.value)) setTempRate('');
              e.target.select();
            }}
            onBlur={commitRate}
            onKeyDown={(e) => e.key === 'Enter' && commitRate()}
            className="w-full h-3.5 px-0.5 text-[9px] tabular-nums text-center border border-blue-400 rounded bg-white"
            autoFocus
            aria-label={`${label} rate percent`}
            data-testid={`override-rate-${fieldKey}`}
          />
        ) : (
          <span
            className={`inline-block w-full text-[9px] tabular-nums text-center text-gray-500 px-0.5 rounded leading-tight ${
              editable ? 'cursor-pointer hover:text-blue-600 hover:bg-blue-50' : 'cursor-default'
            }`}
            onClick={editable ? () => {
              setTempRate(draftOnFocus(rateDisplay));
              setEditingRate(true);
            } : undefined}
            title={baseName ? `${rateDisplay}% of ${baseName}` : undefined}
            data-testid={`override-rate-display-${fieldKey}`}
          >
            {rateDisplay}%
            {baseShort && <span className="block text-[6px] text-gray-400 leading-none">{baseShort}</span>}
          </span>
        )}
      </div>
      <div className="text-right">
        {editable && editingAmount ? (
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={tempAmount}
            onChange={(e) => setTempAmount(e.target.value)}
            onFocus={(e) => {
              if (isEffectivelyZero(e.target.value)) setTempAmount('');
              e.target.select();
            }}
            onBlur={commitAmount}
            onKeyDown={(e) => e.key === 'Enter' && commitAmount()}
            className="w-full h-3.5 px-0.5 text-[9px] tabular-nums text-right border border-blue-400 rounded bg-white"
            autoFocus
            aria-label={`${label} amount`}
            data-testid={`override-amount-${fieldKey}`}
          />
        ) : (
          <ValueWithTooltip fieldKey={fieldKey}>
            <span
              className={`inline-block w-full text-[9px] tabular-nums text-right px-0.5 rounded leading-tight ${
                editable ? 'cursor-pointer hover:text-blue-600 hover:bg-blue-50' : 'cursor-default'
              }`}
              onClick={editable ? () => {
                setTempAmount(draftOnFocus(Math.round(amount || 0).toString()));
                setEditingAmount(true);
              } : undefined}
              data-testid={`override-amount-display-${fieldKey}`}
            >
              {formatMoney(amount, cur)}
            </span>
          </ValueWithTooltip>
        )}
      </div>
    </div>
  );
}
